
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useCalendarEvents } from '../calendar/useCalendarEvents';
import { CustomCalendarHeader } from '../calendar/CustomCalendarHeader';
import { CustomMonthView } from '../calendar/CustomMonthView';
import { EventDetailsPanel } from '../calendar/EventDetailsPanel';
import { CalendarLegend } from '../calendar/CalendarLegend';
import { ScheduleViewProps } from '../types/scheduleTypes';
import { Class, ClassSchedule } from '@/types/classTypes';
import { addMonths, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const MonthlyCalendarView: React.FC<ScheduleViewProps> = ({
  schedules,
  classes,
  onScheduleUpdated,
  onScheduleDeleted,
  initialClassId
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [filterType, setFilterType] = useState<string>(initialClassId || "all");
  const [viewType, setViewType] = useState<'month' | 'week'>('month');
  const [selectedEvent, setSelectedEvent] = useState<{
    class: Class;
    schedules: ClassSchedule[];
  } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const {
    eventsByDate
  } = useCalendarEvents(schedules, classes, selectedDate, selectedMonth, filterType);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };
  
  const handlePrevMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const handleNextMonth = () => {
    setSelectedMonth(addMonths(selectedMonth, 1));
  };

  const handleEventSelect = (classData: Class) => {
    const classSchedules = schedules.filter(s => s.class_id === classData.id);
    setSelectedEvent({
      class: classData,
      schedules: classSchedules
    });
  };

  const handleCloseEventDetails = () => {
    setSelectedEvent(null);
  };

  const handleChangeView = (view: 'month' | 'week') => {
    setViewType(view);
  };

  const handleFullscreenToggle = () => {
    setIsTransitioning(true);
    
    // Add a small delay to show loading state
    setTimeout(() => {
      setIsFullscreen(!isFullscreen);
      // Close event details when entering fullscreen for cleaner view
      if (!isFullscreen) {
        setSelectedEvent(null);
      }
      setIsTransitioning(false);
    }, 150);
  };

  // Keyboard shortcuts for fullscreen mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
      if (event.key === 'f' || event.key === 'F') {
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          handleFullscreenToggle();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  if (isFullscreen) {
    // Create a portal to render outside the app's layout
    return ReactDOM.createPortal(
      <div 
        className="fixed inset-0 z-[9999] bg-white flex flex-col" 
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          width: '100%', 
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Fullscreen Header - Enhanced Mobile Responsive */}
        <div 
          className="bg-gray-900 text-white px-2 sm:px-4 py-2 flex items-center justify-between shrink-0" 
          style={{ 
            minHeight: '56px',
            height: '56px',
            flexShrink: 0
          }}
        >
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-6 flex-1 min-w-0">
            <h1 className="text-sm sm:text-base md:text-lg font-bold truncate">Schedule Calendar</h1>
            
            {/* Month Navigation - Enhanced Touch Targets */}
            <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
              <Button 
                onClick={handlePrevMonth} 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-gray-800 min-w-[44px] min-h-[44px] p-2 touch-manipulation"
                disabled={isTransitioning}
              >
                <span className="text-lg">←</span>
              </Button>
              <span className="text-xs sm:text-sm md:text-base font-semibold min-w-[100px] sm:min-w-[120px] md:min-w-[140px] text-center px-1">
                {selectedMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <Button 
                onClick={handleNextMonth} 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-gray-800 min-w-[44px] min-h-[44px] p-2 touch-manipulation"
                disabled={isTransitioning}
              >
                <span className="text-lg">→</span>
              </Button>
            </div>

            {/* View Toggle - Hidden on very small screens, better touch targets */}
            <div className="hidden sm:flex space-x-1">
              <Button
                onClick={() => handleChangeView('month')}
                variant={viewType === 'month' ? 'secondary' : 'ghost'}
                size="sm"
                className={cn(
                  "min-w-[60px] min-h-[40px] touch-manipulation text-xs sm:text-sm",
                  viewType === 'month' ? 'bg-gray-700 text-white' : 'text-white hover:bg-gray-800'
                )}
                disabled={isTransitioning}
              >
                Month
              </Button>
              <Button
                onClick={() => handleChangeView('week')}
                variant={viewType === 'week' ? 'secondary' : 'ghost'}
                size="sm"
                className={cn(
                  "min-w-[60px] min-h-[40px] touch-manipulation text-xs sm:text-sm",
                  viewType === 'week' ? 'bg-gray-700 text-white' : 'text-white hover:bg-gray-800'
                )}
                disabled={isTransitioning}
              >
                Week
              </Button>
            </div>
          </div>
          
          {/* Exit Control - Enhanced for mobile touch */}
          <div className="flex items-center space-x-2">
            {isTransitioning && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <Button
              onClick={handleFullscreenToggle}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-red-600 min-w-[44px] min-h-[44px] p-2 touch-manipulation"
              disabled={isTransitioning}
            >
              {isTransitioning ? <Minimize2 className="h-5 w-5" /> : <X className="h-5 w-5" />}
          </Button>
          </div>
        </div>

        {/* Fullscreen Calendar Content - TRUE EDGE TO EDGE */}
        <div 
          className="flex-1 w-full overflow-hidden"
          style={{
            height: 'calc(100% - 56px)',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {isTransitioning ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
              <CustomMonthView 
                selectedDate={selectedDate} 
                selectedMonth={selectedMonth} 
                eventsByDate={eventsByDate} 
                onDateSelect={handleDateSelect} 
                onEventSelect={handleEventSelect}
                viewType={viewType}
                isFullscreen={true}
              />
            </div>
          )}
        </div>

        {/* Event Details Overlay - Only if selected */}
        {selectedEvent && (
          <div 
            className="absolute right-0 bg-white border-l-2 border-gray-300 shadow-2xl z-50 overflow-auto"
            style={{
              top: '56px',
              bottom: 0,
              width: 'min(384px, 40vw)',
              maxWidth: '90vw'
            }}
          >
            <EventDetailsPanel 
              selectedEvent={selectedEvent} 
              onClose={handleCloseEventDetails}
              onScheduleUpdated={onScheduleUpdated}
              onScheduleDeleted={onScheduleDeleted}
            />
          </div>
        )}
      </div>,
      document.body
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <CustomCalendarHeader 
          selectedMonth={selectedMonth} 
          viewType={viewType}
          filterType={filterType} 
          classes={classes} 
          onFilterChange={setFilterType}
          onViewChange={handleChangeView}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
        />
        
        {/* Fullscreen Toggle Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleFullscreenToggle}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
                disabled={isTransitioning}
              >
                {isTransitioning ? (
                  <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mr-2"></div>
                ) : (
                  <Maximize2 className="h-4 w-4 mr-2" />
                )}
                Fullscreen View
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Enter immersive calendar view (Ctrl+F or ESC to exit)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <CalendarLegend classes={classes} />
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className={`${selectedEvent ? 'lg:col-span-3' : 'lg:col-span-5'} bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden`}>
          <CustomMonthView 
            selectedDate={selectedDate} 
            selectedMonth={selectedMonth} 
            eventsByDate={eventsByDate} 
            onDateSelect={handleDateSelect} 
            onEventSelect={handleEventSelect}
            viewType={viewType}
          />
        </div>
        
        {selectedEvent && (
          <div className="lg:col-span-2">
            <EventDetailsPanel 
              selectedEvent={selectedEvent} 
              onClose={handleCloseEventDetails}
              onScheduleUpdated={onScheduleUpdated}
              onScheduleDeleted={onScheduleDeleted}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyCalendarView;
